import { ref } from 'vue'
import { loadLocaleMessages } from '@/i18n/admin.js'

export const PUBLIC_LOCALE_COOKIE = 'tamepulse_public_locale'
export const SUPPORTED_PUBLIC_LOCALES = ['en', 'it']
export const DEFAULT_PUBLIC_LOCALE = 'en'

const publicLocale = ref(null)

export const isPublicRoute = (route) => route?.meta?.public === true

export const normalizePublicLocale = (locale) => {
    const code = String(locale || '')
        .trim()
        .toLowerCase()
        .replace('_', '-')
        .split('-')[0]

    return SUPPORTED_PUBLIC_LOCALES.includes(code) ? code : null
}

const readCookie = (name) => {
    if (typeof document === 'undefined') {
        return null
    }

    const cookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`))

    if (!cookie) {
        return null
    }

    return decodeURIComponent(cookie.split('=').slice(1).join('='))
}

const writeCookie = (name, value) => {
    if (typeof document === 'undefined') {
        return
    }

    const attributes = [
        `${name}=${encodeURIComponent(value)}`,
        'Max-Age=31536000',
        'Path=/',
        'SameSite=Lax',
    ]

    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
        attributes.push('Secure')
    }

    document.cookie = attributes.join('; ')
}

export const browserPublicLocale = () => {
    if (typeof navigator === 'undefined') {
        return DEFAULT_PUBLIC_LOCALE
    }

    const browserLocales = [
        ...(navigator.languages || []),
        navigator.language,
    ]

    for (const locale of browserLocales) {
        const normalized = normalizePublicLocale(locale)

        if (normalized) {
            return normalized
        }
    }

    return DEFAULT_PUBLIC_LOCALE
}

export const storedPublicLocale = () => normalizePublicLocale(readCookie(PUBLIC_LOCALE_COOKIE))

export const resolvePublicLocale = () => storedPublicLocale() || browserPublicLocale()

export const setStoredPublicLocale = (locale) => {
    const normalized = normalizePublicLocale(locale) || DEFAULT_PUBLIC_LOCALE

    writeCookie(PUBLIC_LOCALE_COOKIE, normalized)
    publicLocale.value = normalized

    return normalized
}

export const applyPublicLocale = async (locale = resolvePublicLocale()) => {
    const normalized = normalizePublicLocale(locale) || DEFAULT_PUBLIC_LOCALE

    publicLocale.value = normalized

    if (typeof document !== 'undefined') {
        document.documentElement.lang = normalized
    }

    await loadLocaleMessages(normalized)

    return normalized
}

export function usePublicLocale() {
    if (!publicLocale.value) {
        publicLocale.value = resolvePublicLocale()
    }

    const publicLocaleOptions = SUPPORTED_PUBLIC_LOCALES.map((code) => ({
        code,
        labelKey: `common.languages.${code}`,
    }))

    const changePublicLocale = async (locale) => {
        const normalized = setStoredPublicLocale(locale)

        await applyPublicLocale(normalized)

        return normalized
    }

    return {
        publicLocale,
        publicLocaleOptions,
        changePublicLocale,
    }
}
